import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';

export class QuickBuyComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Document | null} */
  #cachedProductHtml = null;
  /** @type {number} */
  #currentQuantity = 1;

  get cachedProductHtml() {
    return this.#cachedProductHtml;
  }

  get productPageUrl() {
    return /** @type {HTMLAnchorElement} */ (this.closest('product-card')?.querySelector('a[ref="productCardLink"]'))
      ?.href;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickBuyModal);
    
    // Listen for cart updates to reset the form
    this.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickBuyModal);
    this.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
  }

  /**
   * Handles cart update events to reset the form
   */
  #handleCartUpdate = (/** @type {CustomEvent} */ event) => {
    if (event.detail.data.didError) return;
    // Reset the form when cart is successfully updated
    this.#resetQuickBuyForm();
  };

  /**
   * Resets the quick buy form to its default state.
   */
  #resetQuickBuyForm() {
    const showFormButton = this.querySelector('.quick-buy__button--show-form');
    const quickBuyForm = this.querySelector('.quick-buy__form');
    
    if (showFormButton && quickBuyForm) {
      showFormButton.classList.remove('hidden');
      quickBuyForm.classList.add('hidden');
      
      // Remove active class from the quick-buy-container parent
      const quickBuyContainer = this.closest('.quick-buy-container');
      if (quickBuyContainer) {
        quickBuyContainer.classList.remove('active');
      }
      
      this.#currentQuantity = 1; // Reset quantity
      this.#updateQuantityDisplay();
      this.#updateQuantityInput();
    }
  }

  /**
   * Shows the quick buy form with quantity selector and add to cart button
   */
  showQuickBuyForm = () => {
    const showFormButton = this.querySelector('.quick-buy__button--show-form');
    const quickBuyForm = this.querySelector('.quick-buy__form');
    
    if (showFormButton && quickBuyForm) {
      showFormButton.classList.add('hidden');
      quickBuyForm.classList.remove('hidden');
      
      // Add active class to the quick-buy-container parent
      const quickBuyContainer = this.closest('.quick-buy-container');
      if (quickBuyContainer) {
        quickBuyContainer.classList.add('active');
      }
      
      // Update the hidden quantity input
      const quantityInput = this.querySelector('input[name="quantity"]');
      if (quantityInput) {
        /** @type {HTMLInputElement} */ (quantityInput).value = this.#currentQuantity.toString();
      }
    }
  };

  /**
   * Increases the quantity
   */
  increaseQuantity = () => {
    this.#currentQuantity = Math.min(this.#currentQuantity + 1, 99);
    this.#updateQuantityDisplay();
    this.#updateQuantityInput();
  };

  /**
   * Decreases the quantity
   */
  decreaseQuantity = () => {
    this.#currentQuantity = Math.max(this.#currentQuantity - 1, 1);
    this.#updateQuantityDisplay();
    this.#updateQuantityInput();
  };

  /**
   * Updates the quantity display
   */
  #updateQuantityDisplay() {
    const quantityDisplay = this.querySelector('.quick-buy__quantity-display');
    if (quantityDisplay) {
      quantityDisplay.textContent = this.#currentQuantity.toString();
    }
  }

  /**
   * Updates the hidden quantity input
   */
  #updateQuantityInput() {
    const quantityInput = this.querySelector('input[name="quantity"]');
    if (quantityInput) {
      /** @type {HTMLInputElement} */ (quantityInput).value = this.#currentQuantity.toString();
    }
  }

  /**
   * Handles quick buy button click for multi-variant products (opens modal)
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    if (!this.#cachedProductHtml) {
      await this.fetchProductPage(this.productPageUrl);
    }

    if (this.#cachedProductHtml) {
      // Create a fresh copy of the cached HTML to avoid modifying the original
      const freshHtmlCopy = new DOMParser().parseFromString(
        this.#cachedProductHtml.documentElement.outerHTML,
        'text/html'
      );

      await this.updateQuickBuyModal(freshHtmlCopy);
    }

    this.#openQuickBuyModal();
  };

  /** @param {QuickBuyDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickBuyModal = () => {
    const dialogComponent = document.getElementById('quick-buy-dialog');
    if (!(dialogComponent instanceof QuickBuyDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();
  };

  #closeQuickBuyModal = () => {
    const dialogComponent = document.getElementById('quick-buy-dialog');
    if (!(dialogComponent instanceof QuickBuyDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<void>}
   * @throws {Error} If the fetch request fails or returns a non-200 response
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      // Store the HTML for later use
      this.#cachedProductHtml = html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   */
  async updateQuickBuyModal(newHtml) {
    const productGrid = newHtml.querySelector('[data-product-grid-content]');
    const modalContent = document.getElementById('quick-buy-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      if (!productDetails) return;
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      if (!productFormComponent || !variantPicker || !productPrice || !productTitle) return;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      productHeader.appendChild(productPrice);
      productGrid.appendChild(productHeader);
      productGrid.appendChild(variantPicker);
      productGrid.appendChild(productFormComponent);
      productDetails.remove();
    }

    morph(modalContent, productGrid);
  }
}

if (!customElements.get('quick-buy-component')) {
  customElements.define('quick-buy-component', QuickBuyComponent);
}

class QuickBuyDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;

    // If this is a new product (different product URL), update the product title text
    // (for products where a different variant is an entirely different product)
    if (event.detail.data.newProduct) {
      const newProductTitle = anchorElement.textContent?.trim();
      if (newProductTitle) {
        if (viewMoreDetailsLink) viewMoreDetailsLink.textContent = newProductTitle;
        if (mobileProductTitle) mobileProductTitle.textContent = newProductTitle;
      }
    }
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-buy-dialog')) {
  customElements.define('quick-buy-dialog', QuickBuyDialog);
}
